import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpSollentunaPage: ServicePageConfig = {
  slug: "flytthjalp-sollentuna",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Sollentuna – Snabb Flytt med Skåpbil | Ungdomskraft",
    description:
      "Flytthjälp i Sollentuna: Tureberg, Edsberg, Väsjön, Häggvik, Rotebro, Norrviken, Helenelund och Silverdal. Skåpbil och bärare, fast pris, RUT.",
    h1: "Flytthjälp Sollentuna",
  },

  hero: {
    heading: "Flytthjälp i Sollentuna",
    intro:
      "Flyttar du till Väsjön, från Tureberg eller mellan villa och lägenhet i Sollentuna? Ungdomskraft kommer med skåpbil och bärare till hela kommunen – Edsberg, Häggvik, Rotebro, Norrviken, Helenelund, Viby, Vaxmora och Sjöberg. Vi bär ut, kör och bär in, och du ser priset redan när du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Flytthjälp i Sollentuna – Ungdomskraft lastar en skåpbil vid ett radhus",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har flyttat hushåll i Stockholms län sedan 2014, och Sollentuna är en av kommunerna där vi är allra oftast. Vi flyttar folk ur lägenheter vid pendeltåget, in i nya kvarter vid Väsjön och ut ur villor vid Edsviken och Norrviken.",
    bookYourText: "Boka flytt i Sollentuna",
    bookDescText:
      "Fyll i adresserna, vad som ska flyttas och vilken dag. Priset syns direkt – skåpbil och bärare ingår.",
    vehicleTitleText: "Skåpbilen följer med",
    vehicleDescText:
      "Vi kör själva mellan adresserna, på E4 eller Norrortsleden, och lastar så att inget glider under färden. Du slipper hyra bil och leta parkering.",
  },

  itemsSection: {
    heading: "Det här flyttar vi i Sollentuna",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffor, divaner och fåtöljer, inlindade för trapphus och hall",
        image: "/sofa.webp",
        alt: "Soffa som bärs ut vid flytthjälp i Tureberg, Sollentuna",
      },
      {
        name: "Sängar & madrasser",
        description: "Sängar som skruvas isär och madrasser i plast",
        image: "/bed.webp",
        alt: "Säng som flyttas vid flytt till Väsjön i Sollentuna",
      },
      {
        name: "Bord & stolar",
        description: "Köksbord, skrivbord och möbler från altan och balkong",
        image: "/table.webp",
        alt: "Köksbord och stolar vid flytt i Rotebro, Sollentuna",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare, kyl och frys",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas vid flytthjälp i Häggvik, Sollentuna",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober, byråer, hyllor och barnrumsmöbler",
        image: "/wardrobe.webp",
        alt: "Garderob som demonteras vid flytt i Edsberg, Sollentuna",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger, förrådsboxar och säckar från källare och garage",
        image: "/box.webp",
        alt: "Flyttkartonger vid villaflytt i Norrviken, Sollentuna",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Sollentuna – pendeltågsnära lägenheter och villor vid sjöarna",
    mainText:
      "Sollentuna är byggt längs pendeltåget. Kommunen har fem stationer – Helenelund, Sollentuna, Häggvik, Norrviken och Rotebro – och kring dem ligger de flesta lägenheterna. I Tureberg, med Sollentuna centrum och kommunhuset, finns flerbostadshus från efterkrigstiden och framåt, där flytten ofta avgörs av hissens storlek och avståndet från porten till lastplatsen. I Rotebro och Häggvik blandas hyreshus och radhus, och i Helenelund har Silverdal vuxit fram som ett nyare bostadsområde nära gränsen mot Kista och Solna.\n\nMellan stationerna ligger villaområden. Norrviken vid sjön med samma namn, Sjöberg och Edsberg vid Edsviken, samt Viby, Vaxmora och Töjnan är typiska villakvarter där flytten gäller hela hus med källare, förråd och garage. Samtidigt byggs det nytt. Kring sjön Väsjön i Edsberg växer en helt ny stadsdel fram, och där flyttar vi många familjer in i nyproduktion – ofta från en mindre lägenhet på annat håll i kommunen eller från Stockholm. Andra vanliga flyttar är villa till lägenhet nära centrum och unga som flyttar hemifrån till en första hyresrätt vid pendeltåget.\n\nE4 går rakt genom Sollentuna med trafikplatser vid bland annat Häggvik och Helenelund, och Norrortsleden leder österut mot Täby. Det gör att vi når hela kommunen snabbt och kan köra vidare till Stockholms innerstad, Upplands Väsby eller Järfälla utan omvägar. Vi planerar alltid lastningen efter adressen: gästparkering vid lägenhetshuset, uppfart vid villan eller en lastgata i nyproduktionen vid Väsjön. Stora villaflyttar tar vi med flera vändor så att allt kommer fram samma dag.",
    preparationHeading: "Förbered flytten i Sollentuna",
    preparationText:
      "Ta ett rum i taget och packa det du inte använder dagligen redan veckan innan. Märk kartongerna med rum och skriv \"ömtåligt\" på dem med porslin eller glas. Samla skruvar och beslag från möbler du tar isär själv i märkta påsar, eller låt oss demontera dem på plats.\n\nBor du i lägenhet i Tureberg, Häggvik eller Rotebro, kolla var bilen kan stå närmast porten och om hissen går att hålla. Flyttar du till Väsjön eller annan nyproduktion, ta reda på om lastningen sker via garage eller en särskild lastplats. I villaområdena hjälper det om uppfarten är tom och garaget öppet. Frosta av kyl och frys dygnet innan och ha nycklar och koder redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Kommundelar i Sollentuna där vi flyttar",
        content:
          "Vi flyttar i hela Sollentuna. I Tureberg kring Sollentuna centrum, i Häggvik och i Rotebro bär vi i flerbostadshus och radhus. I Helenelund flyttar vi både i äldre hus och i Silverdal.\n\nI villaområdena Norrviken, Viby, Vaxmora, Töjnan, Sjöberg och Edsberg tar vi hela hus. Vid Väsjön flyttar vi in familjer i nya kvarter. Körsträcka inom 20 km ingår, vilket täcker flyttar inom Sollentuna och till större delen av Stockholm, Solna och Täby.",
      },
      {
        heading: "Flytta till nya Väsjön",
        content:
          "Inflyttning i en nyproduktion har egna förutsättningar: bokade tider i hissen, lastning via garage och ibland många grannar som flyttar in samma vecka. Skriv vad som gäller i bokningen, så planerar vi bärare, bil och tid efter det. Vi skyddar nya golv och väggar med mattor och filtar och monterar ihop möblerna på plats.\n\nFlytthjälp ger rätt till RUT-avdrag: 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
        cta: { text: "Se bortforsling i Sollentuna →", href: "/bortforsling-sollentuna" },
      },
      {
        heading: "Varför boka flytthjälp i Sollentuna med Ungdomskraft?",
        listItems: [
          {
            label: "Vi är ofta i Sollentuna –",
            text: "Vi har flyttat många hushåll här och känner både lägenhetshusen vid pendeltåget och villagatorna vid sjöarna.",
          },
          {
            label: "E4 genom kommunen –",
            text: "Vi har bilar i hela regionen och tar oss snabbt till Sollentuna, ofta samma vecka och ibland samma dag.",
          },
          {
            label: "Fast pris innan du bokar –",
            text: "Du ser totalpriset direkt. Inga offerter, inga tillägg för trappor.",
          },
          {
            label: "Försäkrad flytt –",
            text: "Bohag, trapphus och hus är försäkrade genom Trygg Hansa under hela flytten.",
          },
          {
            label: "Allt ingår –",
            text: "Skåpbil, bärare, filtar, remmar och golvskydd.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Sollentuna?",
      answer:
        "Några möbler ryms i Liten (995 kr) eller Mellan (1 495 kr). En fullt möblerad etta eller tvåa kostar ofta 2 495–4 995 kr och en hel villa bokas som Mega Stor (8 995 kr). Körning inom 20 km ingår och priset visas innan du bokar.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Sollentuna?",
      answer:
        "Ja, 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
    },
    {
      question: "Hur snabbt kan ni komma till Sollentuna?",
      answer:
        "Vi har ofta lediga tider samma vecka och ibland samma dag. Välj datum i bokningen så ser du vad som finns.",
    },
    {
      question: "Kan ni flytta in oss i nyproduktion vid Väsjön?",
      answer:
        "Ja. Skriv i bokningen om lastning sker via garage och om hissen är bokad en viss tid, så planerar vi efter det.",
    },
    {
      question: "Flyttar ni hela villor i Norrviken, Edsberg eller Viby?",
      answer:
        "Ja, med källare, förråd och garage. Stora hus bokas som Extra Stor eller Mega Stor och vi gör flera vändor om det behövs.",
    },
    {
      question: "Bär ni i lägenhetshus utan hiss?",
      answer: "Ja, vi bär i trapphus med och utan hiss utan extra kostnad.",
    },
    {
      question: "Kan ni flytta från Sollentuna till Stockholm?",
      answer:
        "Ja. Körsträcka inom 20 km ingår, vilket räcker för de flesta flyttar mellan Sollentuna och Stockholm. Går flytten längre räknas resten per kilometer enligt paketets pris.",
    },
    {
      question: "Tar ni isär och sätter ihop möbler?",
      answer:
        "Ja, sängar, garderober och bord demonteras när det behövs och monteras på nya adressen.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, tvättmaskin, torktumlare, kyl, frys och diskmaskin. Frosta av kyl och frys dygnet innan.",
    },
    {
      question: "Kan ni flytta på helgen?",
      answer: "Ja, vi flyttar alla dagar i veckan.",
    },
    {
      question: "Kan ni köra bort saker som inte ska med?",
      answer:
        "Ja, boka en bortforsling samma dag så tar vi det till återvinning eller återbruk.",
    },
    {
      question: "Vilka delar av Sollentuna täcker ni?",
      answer:
        "Hela kommunen: Tureberg, Edsberg med Väsjön, Helenelund med Silverdal, Häggvik, Norrviken, Rotebro, Viby, Vaxmora, Töjnan och Sjöberg.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Vi hjälper till med flytt i hela Stockholmsregionen: ${stockholmKommunerText}. Körsträcka inom 20 km ingår alltid.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Sollentuna i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbilen, skyddar det som behöver skyddas och lastar. Vi kör direkt till nya adressen i Sollentuna eller var den nu ligger.",
    step3Title: "Allt på sin plats",
    step3Desc:
      "Vi bär in allt, ställer möblerna där du vill ha dem och sätter ihop det vi tagit isär.",
    ctaHeading: "Boka flytthjälp i Sollentuna",
    ctaText:
      "Fyll i adresserna, välj paket och tid. Du ser priset direkt och RUT-avdraget dras på fakturan.",
  },

  en: {
    slug: "moving-help-sollentuna",
    meta: {
      title: "Moving Help Sollentuna – Fast Moves by Van | Ungdomskraft",
      description:
        "Moving help in Sollentuna: Tureberg, Edsberg, Väsjön, Häggvik, Rotebro, Norrviken, Helenelund and Silverdal. Van and movers, fixed price, RUT.",
      h1: "Moving Help Sollentuna",
    },
    hero: {
      heading: "Moving Help in Sollentuna",
      intro:
        "Moving to Väsjön, out of Tureberg or between a house and a flat in Sollentuna? Ungdomskraft brings a van and movers to the whole municipality — Edsberg, Häggvik, Rotebro, Norrviken, Helenelund, Viby, Vaxmora and Sjöberg. We carry out, drive and carry in, and you see the price as soon as you book.",
      heroImageAlt: "Moving help in Sollentuna — Ungdomskraft loading a van outside a terraced house",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has moved households in Stockholm County since 2014, and Sollentuna is one of the municipalities where we work most often. We move people out of flats by the commuter train, into new blocks at Väsjön and out of houses by Edsviken and Norrviken.",
      bookYourText: "Book a move in Sollentuna",
      bookDescText:
        "Fill in the addresses, what's moving and which day. The price shows instantly — van and movers included.",
      vehicleTitleText: "The van comes along",
      vehicleDescText:
        "We drive between the addresses ourselves, on the E4 or Norrortsleden, and load so nothing shifts on the way. No rental van and no hunting for parking.",
    },
    itemsSection: {
      heading: "What we move in Sollentuna",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Sofas, daybeds and armchairs, wrapped for stairwell and hallway",
          image: "/sofa.webp",
          alt: "Sofa carried out during moving help in Tureberg, Sollentuna",
        },
        {
          name: "Beds & mattresses",
          description: "Beds unscrewed for transport and mattresses in plastic",
          image: "/bed.webp",
          alt: "Bed moved during a move to Väsjön in Sollentuna",
        },
        {
          name: "Tables & chairs",
          description: "Kitchen tables, desks and furniture from deck and balcony",
          image: "/table.webp",
          alt: "Kitchen table and chairs during a move in Rotebro, Sollentuna",
        },
        {
          name: "Appliances",
          description: "Washing machine, dryer, fridge and freezer",
          image: "/washer.webp",
          alt: "Washing machine moved during moving help in Häggvik, Sollentuna",
        },
        {
          name: "Wardrobes & dressers",
          description: "Wardrobes, dressers, shelving and children's room furniture",
          image: "/wardrobe.webp",
          alt: "Wardrobe dismantled during a move in Edsberg, Sollentuna",
        },
        {
          name: "Moving boxes",
          description: "Boxes, storage tubs and bags from basement and garage",
          image: "/box.webp",
          alt: "Moving boxes during a house move in Norrviken, Sollentuna",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Sollentuna — flats by the commuter train and houses by the lakes",
      mainText:
        "Sollentuna is built along the commuter train. The municipality has five stations — Helenelund, Sollentuna, Häggvik, Norrviken and Rotebro — and most of the flats lie around them. In Tureberg, with Sollentuna centrum and the town hall, there are apartment blocks from the post-war era onwards, where the move often comes down to the size of the lift and the distance from the entrance to the loading spot. Rotebro and Häggvik mix rental blocks and terraced houses, and in Helenelund, Silverdal has grown into a newer residential area near the border with Kista and Solna.\n\nBetween the stations lie house areas. Norrviken by the lake of the same name, Sjöberg and Edsberg by Edsviken, and Viby, Vaxmora and Töjnan are typical house neighbourhoods where the move covers whole houses with basement, storage and garage. At the same time there's new building. Around Lake Väsjön in Edsberg a whole new district is taking shape, and there we move many families into new builds — often from a smaller flat elsewhere in the municipality or from Stockholm. Other common moves are house to flat near the centre and young people leaving home for a first rental flat by the commuter train.\n\nThe E4 runs straight through Sollentuna with junctions at Häggvik and Helenelund among others, and Norrortsleden leads east towards Täby. That means we reach the whole municipality quickly and can drive on to central Stockholm, Upplands Väsby or Järfälla without detours. We always plan the loading around the address: guest parking by the apartment block, a driveway at the house or a loading street at the Väsjön new builds. Large house moves are done in several trips so everything arrives the same day.",
      preparationHeading: "Prepare your move in Sollentuna",
      preparationText:
        "Take one room at a time and pack what you don't use daily the week before. Label boxes by room and write \"fragile\" on those with china or glass. Keep screws and fittings from furniture you dismantle yourself in labelled bags, or let us take it apart on site.\n\nIf you live in a flat in Tureberg, Häggvik or Rotebro, check where the van can park closest to the entrance and whether the lift can be held. Moving to Väsjön or another new build? Find out whether loading goes via the garage or a dedicated loading spot. In the house areas it helps if the driveway is clear and the garage open. Defrost the fridge and freezer the day before and have keys and codes ready for both addresses.",
      additionalSections: [
        {
          heading: "Districts in Sollentuna where we move",
          content:
            "We move across the whole of Sollentuna. In Tureberg around Sollentuna centrum, in Häggvik and in Rotebro we carry in apartment blocks and terraced houses. In Helenelund we move both in older buildings and in Silverdal.\n\nIn the house areas of Norrviken, Viby, Vaxmora, Töjnan, Sjöberg and Edsberg we take whole houses. At Väsjön we move families into new blocks. Driving within 20 km is included, which covers moves within Sollentuna and to most of Stockholm, Solna and Täby.",
        },
        {
          heading: "Moving to the new Väsjön",
          content:
            "Moving into a new build has its own conditions: booked lift times, loading via the garage and sometimes lots of neighbours moving in the same week. Note what applies in the booking and we'll plan movers, van and timing around it. We protect new floors and walls with mats and blankets and assemble the furniture on site.\n\nMoving help qualifies for the RUT tax deduction: 50% of the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
          cta: { text: "See junk removal in Sollentuna →", href: "/en/junk-removal-sollentuna" },
        },
        {
          heading: "Why book moving help in Sollentuna with Ungdomskraft?",
          listItems: [
            {
              label: "We're often in Sollentuna –",
              text: "We've moved many households here and know both the apartment blocks by the commuter train and the house streets by the lakes.",
            },
            {
              label: "The E4 runs through –",
              text: "We have vans across the region and get to Sollentuna quickly, often the same week and sometimes the same day.",
            },
            {
              label: "Fixed price before you book –",
              text: "You see the total instantly. No quotes, no stair surcharge.",
            },
            {
              label: "Insured move –",
              text: "Belongings, stairwell and house are insured through Trygg Hansa throughout the move.",
            },
            {
              label: "Everything included –",
              text: "Van, movers, blankets, straps and floor protection.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Sollentuna cost?",
        answer:
          "A few pieces fit Small (SEK 995) or Medium (SEK 1,495). A fully furnished studio or one-bed often costs SEK 2,495–4,995 and a whole house is booked as Mega Large (SEK 8,995). Driving within 20 km is included and the price shows before you book.",
      },
      {
        question: "Can I get the RUT deduction on moving help in Sollentuna?",
        answer:
          "Yes, a 50% deduction on the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
      },
      {
        question: "How quickly can you come to Sollentuna?",
        answer:
          "We often have slots the same week and sometimes the same day. Pick a date in the booking to see what's available.",
      },
      {
        question: "Can you move us into a new build at Väsjön?",
        answer:
          "Yes. Note in the booking whether loading goes via the garage and whether the lift is booked for a set time, and we'll plan around it.",
      },
      {
        question: "Do you move whole houses in Norrviken, Edsberg or Viby?",
        answer:
          "Yes, with basement, storage and garage. Large houses are booked as Extra Large or Mega Large and we make several trips if needed.",
      },
      {
        question: "Do you carry in apartment blocks without a lift?",
        answer: "Yes, we carry in stairwells with and without lifts at no extra cost.",
      },
      {
        question: "Can you move me from Sollentuna to Stockholm?",
        answer:
          "Yes. Driving within 20 km is included, which covers most moves between Sollentuna and Stockholm. For longer moves the rest is charged per kilometre at the package rate.",
      },
      {
        question: "Do you take apart and reassemble furniture?",
        answer:
          "Yes, beds, wardrobes and tables are dismantled when needed and reassembled at the new address.",
      },
      {
        question: "Do you move appliances?",
        answer:
          "Yes — washing machines, dryers, fridges, freezers and dishwashers. Defrost the fridge and freezer the day before.",
      },
      {
        question: "Can you move at the weekend?",
        answer: "Yes, we move every day of the week.",
      },
      {
        question: "Can you take away things that aren't coming?",
        answer:
          "Yes, book a junk removal for the same day and we'll take it to recycling or reuse.",
      },
      {
        question: "Which parts of Sollentuna do you cover?",
        answer:
          "The whole municipality: Tureberg, Edsberg with Väsjön, Helenelund with Silverdal, Häggvik, Norrviken, Rotebro, Viby, Vaxmora, Töjnan and Sjöberg.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `We help with moves across the Stockholm region: ${stockholmKommunerText}. Driving within 20 km is always included.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Sollentuna in 3 steps",
      step2Text:
        "The movers arrive with the van, protect what needs protecting and load. We drive straight to the new address in Sollentuna or wherever it is.",
      step3Title: "Everything in its place",
      step3Desc:
        "We carry everything in, put the furniture where you want it and reassemble what we took apart.",
      ctaHeading: "Book moving help in Sollentuna",
      ctaText:
        "Fill in the addresses, choose a package and a time. You see the price instantly and the RUT deduction comes off the invoice.",
    },
  },
};
